<template>
  <v-card id="quota-detail">
    <v-card-title class="primary">
      <span class="white--text headline">{{titleText}}</span>
      <v-spacer/>
      <BaseDialogActions hideRefresh customCancel @close="checkPendingEdits()"/>
    </v-card-title>
    <v-card-text>
      <v-container fluid grid-list-lg>
        <DataTable
          :items.sync="contractAccountsMapped"
          :headers="columnSet"
          :customCells="customColumns"
          :actions="tableActions"
          :disabled="!userAssignedClaim(UserClaims.ContractManager)"
          @refresh="refreshQuotas()"
          @edit="enableEdit()"
          @save="saveEdits()"
          @cancel="cancelAllEdits()"
          class="pt-2"
        >
          <template #name="{item}">
            <v-row align-content="center" align="center" no-gutters>
              <v-col cols="auto">
                {{ item.name || $t('notAvailable') }}
              </v-col>
              <v-col>
                <Icon
                  v-if="item.quota == null"
                  iconColor="warning"
                  margin="mr-3"
                  icon="mdi-exclamation"
                  :small="false"
                  :tooltipText="$t('quotaNotDefined')"
                />
              </v-col>
            </v-row>
          </template>
          <template #allocated="{item}">
            <span v-if="item.quota !== null || editingQuotas">
              <span class="mr-2" v-if="item.hasContractInEffectThisPeriod && !editingQuotas && item.quota.quotaInEffect !== null && item.quota.quotaInEffect !== undefined">
                {{ item.quotaInEffectForEdit }}
              </span>
              <Icon
                v-else-if="!editingQuotas && item.hasContractInEffectThisPeriod"
                icon="mdi-alert-circle-outline"
                iconColor="black"
                :tooltipText="$t('noQuotaAllocated', { period: $t('current') })"
                :small="false"
              />
              <v-text-field
                v-if="item.hasContractInEffectThisPeriod && editingQuotas"
                v-model="item.quotaInEffectForEdit"
                ref="quotaInEffect"
                height="25"
                type="text"
                maxlength="4"
                :readonly="!editingQuotas"
                :rules="[rules.positiveIntegerBetweenOrBlank(0, 1000)]"
                style="max-width: 4rem; justify-self: end;"
              >
                <template #append>
                  <Icon
                    v-if="item.quotaInEffectForEdit !== item.quotaInEffect"
                    icon="mdi-arrow-u-left-top"
                    iconColor="error"
                    :tooltipText="$t('revert')"
                    @icon-clicked="cancelEditInEffect(item)"
                  />
                </template>
              </v-text-field>
              <Icon
                v-if="!item.hasContractInEffectThisPeriod"
                icon="mdi-clock"
                iconColor="black"
                :small="false"
                :tooltipText="$t('noContractInEffectThisPeriod')"
              />
            </span>
          </template>
          <template #planned="{item}">
            <span v-if="item.quota !== null || editingQuotas">
              <span class="mr-2" v-if="item.hasContractInEffectNextPeriod && !editingQuotas && item.quota.quotaNextPeriod !== null && item.quota.quotaNextPeriod !== undefined">
                {{ item.quotaNextPeriodForEdit }}
              </span>
              <Icon
                v-else-if="!editingQuotas && item.hasContractInEffectNextPeriod"
                icon="mdi-alert-circle-outline"
                iconColor="black"
                :tooltipText="$t('noQuotaAllocated', { period: $t('next') })"
                :small="false"
              />
              <v-text-field
                v-if="item.hasContractInEffectNextPeriod && editingQuotas"
                v-model="item.quotaNextPeriodForEdit"
                ref="quotaNextPeriod"
                height="25"
                type="text"
                maxlength="4"
                :readonly="!editingQuotas"
                :rules="[rules.positiveIntegerBetweenOrBlank(0, 1000)]"
                style="max-width: 4rem;justify-self: end;"
              >
                <template #append>
                  <Icon
                    v-if="item.quotaNextPeriodForEdit !== item?.quotaNextPeriod"
                    icon="mdi-arrow-u-left-top"
                    iconColor="error"
                    :tooltipText="$t('revert')"
                    @icon-clicked="cancelEditNextPeriod(item)"
                  />
                </template>
              </v-text-field>
              <Icon
                  v-if="!item.hasContractInEffectNextPeriod"
                  icon="mdi-clock"
                  iconColor="black"
                  :small="false"
                  :tooltipText="$t('noContractInEffectNextPeriod')"
                />
            </span>
          </template>
          <template #created="{item}">
            <span class="mr-2" v-if="item.quota !== null && item?.quota?.quotaInEffect !== null">{{ item.quota?.quotaCurrentCount ?? 0 }}</span>
          </template>
          <template #remaining="{item}">
            <v-row v-if="item.quota !== null && item?.quota?.quotaInEffect !== null" align="center" justify="end" no-gutters>
              <v-col :class="determineAlertIcon(item.quota) ? '' : 'mr-5'">
                {{ calculateRemainingQuota(item.quota) }}
              </v-col>
              <v-col cols="auto" v-if="determineAlertIcon(item.quota)">
                <Icon
                  :icon="determineAlertIcon(item.quota).icon"
                  :iconColor="determineAlertIcon(item.quota).color"
                  :tooltipText="determineAlertIcon(item.quota).text"
                  margin="ml-1"
                />
              </v-col>
            </v-row>
          </template>
        </DataTable>
      </v-container>
    </v-card-text>
    <Dialog :stateId="dialogId"
      maxWidth="20vw"
      @click:outside="closeDialogs()"
      @dialog-closing="closeDialogs()"
      @close="closeDialogs()"
    >
      <ConfirmDialog
        v-if="confirmDialog"
        :title="$t('closeDialogWithoutSavingTitle')"
        :body="$t('closeDialogWithoutSavingWarning')"
        :confirmText="$t('close')"
        width="20vw"
        @cancel="closeDialogs()"
        @confirm="closeQuotaDetail()"
      />
    </Dialog>
  </v-card>
</template>

<script>
import { uniqueDialogId } from '@/utils/componentHelpers'
import quotaHeaders from '@/headers/Quota.js'
import fieldRules from '@/utils/rules.js'
import { mapActions, mapGetters } from 'vuex'
import { UserClaims } from '@/utils/Enumerations.js'
import { userAssignedClaim } from '@/utils/ClaimUtility.js'
export default {
  name: 'QuotaDetail',

  components: {
    Icon: () => import('@/components/helper/Icon.vue'),
    ConfirmDialog: () => import('@/components/helper/ConfirmDialog.vue'),
    Dialog: () => import('@/components/Dialog.vue'),
    DataTable: () => import('@/components/core/table/DataTable.vue'),
    BaseDialogActions: () => import('@/components/core/BaseDialogActions.vue')
  },

  props: {
    contractAccounts: {
      type: Array,
      required: true
    },
    destination: {
      type: Object,
      required: true
    }
  },

  data: () => ({
    dialogId: uniqueDialogId('quota-detail'),
    editingQuotas: false,
    contractAccountsMapped: [],
    columnSet: null,
    confirmDialog: false,
    rules: fieldRules.rules,
    UserClaims
  }),

  watch: {
    contractAccounts: {
      handler () {
        this.$nextTick(_ => {
          this.mapContractAccounts()
        })
      }
    }
  },

  computed: {
    ...mapGetters('dialog', ['topDialogState']),
    titleText () {
      return this.$t('assignQuotaToAccount', { destinationAccountName: this.destination.name, destinationQuotaPeriod: this.destination.quotaPeriodDate })
    },

    tableActions () {
      return [
        {
          actionName: 'refresh',
          icon: 'mdi-refresh',
          text: this.$t('refresh'),
          id: 'refresh'
        },
        !this.editingQuotas ? {
          actionName: 'edit',
          icon: 'mdi-pencil',
          text: this.$t('edit'),
          id: 'edit-quotas'
        } : undefined,
        this.editingQuotas ? {
          actionName: 'save',
          icon: 'mdi-content-save-all',
          text: this.$t('save'),
          iconColor: 'black',
          id: 'save-quotas'
        } : undefined,
        this.editingQuotas ? {
          actionName: 'cancel',
          icon: 'mdi-pencil-off',
          text: this.$t('cancelEdit'),
          iconColor: 'black',
          id: 'cancel'
        } : undefined
      ].filter(ta => ta)
    },

    quotaDialogHeaders () {
      return quotaHeaders.quotaDialogHeaders()
    },

    customColumns () {
      return [
        {
          slotName: 'name',
          value: 'name'
        },
        {
          slotName: 'allocated',
          value: 'quotaInEffect'
        },
        {
          slotName: 'planned',
          value: 'quotaNextPeriod'
        },
        {
          slotName: 'created',
          value: 'quotaCurrentCount'
        },
        {
          slotName: 'remaining',
          value: 'quotaRemaining'
        }
      ]
    }
  },

  async created () {
    this.columnSet = this.quotaDialogHeaders.getColumns()
    this.mapContractAccounts()
  },

  methods: {
    ...mapActions('dialog', ['openOrUpdateDialog', 'closeDialogsAtOrAbove']),
    ...mapActions('quotas', ['createQuota', 'updateQuota']),
    userAssignedClaim,

    mapContractAccounts () {
      this.contractAccountsMapped = this.contractAccounts.map(ca => ({
        ...ca,
        quotaInEffect: ca.quota?.quotaInEffect,
        quotaNextPeriod: ca.quota?.quotaNextPeriod,
        quotaInEffectForEdit: ca.quota?.quotaInEffect,
        quotaNextPeriodForEdit: ca.quota?.quotaNextPeriod,
        quotaCurrentCount: ca.quota?.quotaCurrentCount,
        quotaRemaining: ca.quota?.quotaInEffect - ca.quota?.quotaCurrentCount
      }))
    },

    enableEdit () {
      this.editingQuotas = true
      this.$emit('editing', true)
    },

    async saveEdits () {
      this.editingQuotas = false
      await Promise.all(
        this.contractAccountsMapped.map(async (account) => {
          await this.editQuotaForContractAccount(account)
        })
      )
      this.refreshQuotas()
      this.$emit('editing', false)
    },

    cancelAllEdits () {
      this.editingQuotas = false
      this.mapContractAccounts()
      this.$emit('editing', false)
    },

    cancelEditNextPeriod (contractAccount) {
      contractAccount.editQuotaNextPeriod = false
      contractAccount.quotaNextPeriodForEdit = contractAccount?.quotaNextPeriod
    },

    cancelEditInEffect (contractAccount) {
      contractAccount.editQuotaInEffect = false
      contractAccount.quotaInEffectForEdit = contractAccount?.quotaInEffect
    },

    async editQuotaForContractAccount (contractAccount) {
      if (contractAccount.quotaNextPeriodForEdit !== '' &&
      contractAccount.quota?.quotaNextPeriod === Number(contractAccount.quotaNextPeriodForEdit) &&
      contractAccount.quotaInEffectForEdit !== '' &&
      contractAccount.quota?.quotaInEffect === Number(contractAccount.quotaInEffectForEdit)) {
        return
      }
      if (contractAccount.quota?.quotaId) {
        contractAccount.quota.quotaNextPeriod = contractAccount.hasContractInEffectNextPeriod && contractAccount.quotaNextPeriodForEdit ? Number(contractAccount.quotaNextPeriodForEdit) : null
        contractAccount.quota.quotaInEffect = contractAccount.hasContractInEffectThisPeriod && contractAccount.quotaInEffectForEdit ? Number(contractAccount.quotaInEffectForEdit) : null
        await this.updateQuota({ quota: contractAccount.quota })
      } else {
        const quota = this.createQuotaObject(contractAccount)
        await this.createQuota({ quota: quota })
      }
    },

    createQuotaObject (contractAccount) {
      const sanitizedQuotaInEffect = contractAccount.hasContractInEffectThisPeriod && contractAccount.quotaInEffectForEdit ? Number(contractAccount.quotaInEffectForEdit) : null
      const sanitizedQuotaNextPeriod = contractAccount.hasContractInEffectNextPeriod && contractAccount.quotaNextPeriodForEdit ? Number(contractAccount.quotaNextPeriodForEdit) : null
      return {
        accountId: contractAccount.accountId,
        destinationAccountId: this.destination.id,
        quotaInEffect: sanitizedQuotaInEffect,
        quotaNextPeriod: sanitizedQuotaNextPeriod,
        quotaCurrentCount: undefined
      }
    },

    refreshQuotas () {
      this.$emit('refresh')
    },

    checkPendingEdits () {
      if (this.editingQuotas) {
        this.confirmDialog = true
        this.openOrUpdateDialog({ id: this.dialogId, width: '20vw' })
      } else {
        this.closeQuotaDetail()
      }
    },

    closeDialogs () {
      this.confirmDialog = false
      this.closeDialogsAtOrAbove(this.dialogId)
    },

    closeQuotaDetail () {
      this.closeDialogs()
      this.editingQuotas = false
      this.$emit('close')
    },

    calculateRemainingQuota (quota) {
      const amount = quota?.quotaInEffect - quota?.quotaCurrentCount
      if (isNaN(amount)) return 0
      return amount < 0 ? 0 : amount
    },

    determineAlertIcon (quota) {
      const amount = quota?.quotaInEffect - quota?.quotaCurrentCount
      if (isNaN(amount) || amount > 0) return undefined
      return {
        icon: amount < 0 ? 'mdi-alert' : 'mdi-alert-outline',
        color: amount < 0 ? 'error' : 'warning',
        text: amount < 0 ? this.$i18n.t('loadsOverQuota', { x: Math.abs(amount) }) : this.$i18n.t('loadsRemaining', { x: amount })
      }
    }
  }
}
</script>
